import { formatCZK } from "@/utils/MyEstateHelper";
import { TrendingUp, TrendingDown, Wallet } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Panel } from "@/components/ui/Panel";

export type FinanceCardProps = {
  type: "INCOME" | "EXPENSE" | "PROFIT";
  value: number;
  total: number;
  description?: string;
  bg?: string;
};

const typeConfig = {
  INCOME: {
    Icon: TrendingUp,
    text: "text-emerald-700 dark:text-emerald-300",
    barBg: "bg-emerald-500/10",
    bar: "bg-emerald-500",
    iconBg: "bg-emerald-500/10",
    iconText: "text-emerald-600 dark:text-emerald-400",
  },
  EXPENSE: {
    Icon: TrendingDown,
    text: "text-red-700 dark:text-red-300",
    barBg: "bg-red-500/10",
    bar: "bg-red-500",
    iconBg: "bg-red-500/10",
    iconText: "text-red-600 dark:text-red-400",
  },
  PROFIT: {
    Icon: Wallet,
    text: null,
    barBg: "bg-softiq-primary/10 dark:bg-white/10",
    bar: null,
    iconBg: "bg-softiq-primary/10 dark:bg-white/10",
    iconText: "text-softiq-primary dark:text-softiq-secondary",
  },
};

const FinanceCard = ({
  type,
  value,
  total,
  description = "",
  bg = "",
}: FinanceCardProps) => {
  const { language } = useLanguage();
  const copy = {
    cs: {
      labels: {
        INCOME: "Příjmy",
        EXPENSE: "Výdaje",
        PROFIT: "Výsledek hospodaření",
      },
    },
    en: {
      labels: {
        INCOME: "Income",
        EXPENSE: "Expenses",
        PROFIT: "Operating result",
      },
    },
  }[language];
  const { Icon, iconBg, iconText, barBg } = typeConfig[type];
  const label = copy.labels[type];

  const text =
    type === "PROFIT"
      ? value >= 0
        ? "text-emerald-700 dark:text-emerald-300"
        : "text-red-700 dark:text-red-300"
      : typeConfig[type].text!;

  const bar =
    type === "PROFIT"
      ? value >= 0
        ? "bg-emerald-500"
        : "bg-red-500"
      : typeConfig[type].bar!;

  const barWidth = Math.min(
    (Math.abs(value) / Math.max(Math.abs(total), Math.abs(value), 1)) * 100,
    100,
  );

  return (
    <Panel className={`flex min-w-0 flex-1 flex-col overflow-hidden ${bg}`}>
      <div className="flex items-center gap-3">
        <div
          className={`flex size-9 shrink-0 items-center justify-center rounded-lg ${iconBg} ${iconText}`}
        >
          <Icon className="size-5" />
        </div>
        <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-softiq-primary/50 dark:text-white/50">
          {label}
        </p>
      </div>

      <div className="mt-4 space-y-1">
        <p className={`text-xl font-semibold tracking-tight ${text}`}>
          {formatCZK(value)}
        </p>
        {description ? (
          <p className="app-card-description">{description}</p>
        ) : null}
      </div>

      <div
        className={`mt-4 h-1.5 w-full overflow-hidden rounded-full ${barBg}`}
      >
        <div
          className={`h-full rounded-full ${bar} transition-all duration-500`}
          style={{ width: `${barWidth}%` }}
        />
      </div>
    </Panel>
  );
};

export default FinanceCard;
